import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import CustomInput from "../../components/input/CustomInput";
import { motion } from "framer-motion";
import { Eye, SlashedEye } from "../../components/svg/Auth/Auth";
import { useAuth } from "../../api/auth";
import Button from "../../components/button/Button";
import clsx from "clsx";
import { Loader } from "../../components/loader/Loader";
import { AuthLayout } from "../../layout/AuthLayout";
import { useForgotPassword } from "../../api/forgotPassword";

const rules = [
  { key: "lowercase", label: "One lowercase character" },
  { key: "uppercase", label: "One uppercase character" },
  { key: "number", label: "One number" },
  { key: "special", label: "One special  character" },
  { key: "length", label: "Min 8 characters" },
];

function SetPassword() {
  const [viewPassword, setViewPassword] = useState(false);
  const [type, setType] = useState("");
  const search = useLocation();
  const searchParams = new URLSearchParams(search.search);
  const [passwordsMatch, setPasswordsMatch] = useState(false);

  const [valid, setValid] = useState({
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
    length: false,
  });

  const {
    handleSignUp,
    setEmail,
    password,
    setPassword,
    confirm_password,
    setConfirmPassword,
    referralCode,
    setReferralCode,
    loading,
  } = useAuth();

  const {
    handleChangePassword,
    setEmailAddress,
    pending,
    setFPassword,
    setConfirmFPassword,
  } = useForgotPassword();

  useEffect(() => {
    if (searchParams) {
      setEmail(searchParams.get("mobile"));
      setEmailAddress(searchParams.get("mobile"));
      setType(searchParams.get("type"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validatePassword = (value) => {
    setPassword(value);
    const newValid = {
      lowercase: /[a-z]/.test(value),
      uppercase: /[A-Z]/.test(value),
      number: /[0-9]/.test(value),
      special: /[_!@#$%^&*(),.?":{}|<>]/.test(value),
      length: value.length >= 8,
    };
    setValid(newValid);
  };

  useEffect(() => {
    if (password && confirm_password) {
      setPasswordsMatch(password === confirm_password);
    } else {
      setPasswordsMatch(false);
    }
  }, [password, confirm_password]);

  const allRulesPassed = Object.values(valid).every((rule) => rule === true);
  const isFormValid =
    password.trim() !== "" && allRulesPassed && passwordsMatch;

  return (
    <AuthLayout>
      <div className="container">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className=" flex flex-col items-center justify-center rounded-b-[50px]"
        >
          <img
            className="max-w-[102px] object-contain mt-32 mb-10"
            src="/image/Hand-3d.png"
            alt="banner"
            loading="lazy"
          />
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-2xl tracking-tight font-medium text-center text-black mx-0"
          >
            {type ? "Set Password" : "Complete Setup"}
          </motion.h3>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="mt-7"
        >
          {!type && (
            <div>
              <CustomInput
                placeholder="Enter referral code"
                type="text"
                name="referral_code"
                value={referralCode}
                onchange={(e) => {
                  setReferralCode(e.target.value);
                }}
                inputLabel={referralCode && "Referral code"}
              />
            </div>
          )}
          <div className={!type && "mt-4"}>
            <CustomInput
              placeholder="Password"
              type={viewPassword ? "text" : "password"}
              name="password"
              value={password}
              autoComplete="off"
              required={true}
              trailingIcon={viewPassword ? <Eye /> : <SlashedEye />}
              onclick={() => setViewPassword(!viewPassword)}
              onchange={(e) => {
                validatePassword(e.target.value);
                setFPassword(e.target.value);
              }}
              inputLabel={password && "Password"}
            />
          </div>
          <div className="mt-4">
            <CustomInput
              placeholder="Confirm Password"
              type={viewPassword ? "text" : "password"}
              name="password"
              value={confirm_password}
              autoComplete="off"
              required={true}
              trailingIcon={viewPassword ? <Eye /> : <SlashedEye />}
              onclick={() => setViewPassword(!viewPassword)}
              onchange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmFPassword(e.target.value);
              }}
              inputLabel={confirm_password && "Confirm Password"}
            />
          </div>
          <div>
            <div className="grid grid-cols-6 gap-x-2 gap-y-0 mt-4 text-sm">
              {rules.map(({ key, label }) => (
                <div
                  key={key}
                  className={clsx(
                    "flex items-center gap-2 leading-tight col-span-6 400:col-span-3",
                    key === "length" && "col-span-2"
                  )}
                >
                  <span
                    className={clsx(
                      "h-1 w-1 rounded-full",
                      valid[key] ? "bg-green-600" : "bg-gray-300"
                    )}
                  />
                  <span
                    className={clsx(
                      valid[key] ? "text-black" : "text-gray-400",
                      "text-xs"
                    )}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
            {confirm_password && (
              <div className="flex items-center gap-2 text-sm mt-3">
                <span
                  className={clsx(
                    "h-1 w-1 rounded-full",
                    passwordsMatch ? "bg-green-600" : "bg-red-500"
                  )}
                />
                <span
                  className={clsx(
                    passwordsMatch ? "text-green-600" : "text-red-500",
                    "text-xs"
                  )}
                >
                  {passwordsMatch ? "Passwords match" : "Passwords don't match"}
                </span>
              </div>
            )}
          </div>
          <Button
            text={loading || pending ? <Loader type="bubbles" /> : "Confirm"}
            className={clsx((loading || pending) && "!py-0", "mt-5")}
            onClick={() => {
              if (type) {
                handleChangePassword();
              } else {
                handleSignUp();
              }
            }}
            disabled={!isFormValid}
          />
        </motion.div>
      </div>
    </AuthLayout>
  );
}

export default SetPassword;
